import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import ProjectPublishedModal from '../ProjectPublishedModal';
import ProjectLogisticsInfo from '../common/ProjectLogisticsInfo';
import { analyticsTrack } from '../../analyticsUtils';
import loading from '../../../assets/images/spinner-DNA-2s-200px.svg';
import { deleteRequest, putRequest } from '../../requestUtils';
import { formatCurrency } from '../../numberUtils';
import { decodeAmpersands } from '../../responsibilitiesUtils';

class ProjectSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showAdminInfo: false,
    };
    this.handleDeleteProject = this.handleDeleteProject.bind(this);
    this.handlePublishProject = this.handlePublishProject.bind(this);
    this.handleUnpublishProject = this.handleUnpublishProject.bind(this);
    this.handleDuplicateProject = this.handleDuplicateProject.bind(this);
    this.handleStepNavigation = this.handleStepNavigation.bind(this);
    this.toggleShowAdminInfo = this.toggleShowAdminInfo.bind(this);
  }

  componentDidMount() {
    analyticsTrack('Viewed project details tab', {
      location: 'Projects view',
    });
  }

  toggleShowAdminInfo(event) {
    event.preventDefault();

    this.setState((prevState) => ({
      showAdminInfo: !prevState.showAdminInfo,
    }));
  }

  handleDeleteProject() {
    const { projectResourceId, csrfToken } = this.props;

    deleteRequest(`/projects/${projectResourceId}`, csrfToken);
  }

  handlePublishProject() {
    const { projectResourceId, csrfToken } = this.props;

    putRequest(`/projects/${projectResourceId}/publish`, {}, csrfToken);
  }

  handleStepNavigation(title, step = 'start') {
    const { projectResourceId, permissions } = this.props;

    return (
      <Fragment>
        {
          permissions.update
            && (
            <a
              className="edit-logistics-button"
              data-toggle="tooltip"
              data-placement="bottom"
              title={title}
              href={`/projects/${projectResourceId}/edit?from_project_page=true&step=${step}`}
            >
              <i className="fa fa-edit" />
            </a>
            )
        }
      </Fragment>
    );
  }

  handleUnpublishProject() {
    const { projectResourceId, csrfToken } = this.props;

    putRequest(`/projects/${projectResourceId}/unpublish`, {}, csrfToken);
  }

  handleDuplicateProject() {
    const { projectResourceId, csrfToken } = this.props;

    putRequest(`/projects/${projectResourceId}/duplicate`, {}, csrfToken).then((response) => {
      window.location = response.data.url;
    });
  }

  renderEditLink(label, section, id, permission = 'update', className = 'required', title = null) {
    const { permissions, projectResourceId } = this.props;
    const data = title ? { placement: 'bottom', toggle: 'tooltip' } : {};
    if (!permissions[permission]) {
      return (
        <h2 className="project-section-title">{label}</h2>
      );
    }

    let url = `/projects/${projectResourceId}/edit?from_project_page=true`;
    if (section) {
      url = [url, `step=${section}`].join('&');
    }

    return (
      <a href={url} id={id} className={className} title={title} data={data}>
        { label }
      </a>
    );
  }

  renderErrors() {
    const { permissions, project } = this.props;

    const errors = project.error_messages;
    const showRequestMatching = (
      project.status === 'employer_draft'
      && project.error_messages.length < 1
      && permissions.publish
    );

    return (
      <div className="project-error-container white-container box-shadow-1  mb-3  ">
        <div className="g-row">
          <div className="col-sm-9">
            <div id="error_explanation" className="style-guide">
              <h3 className="demibold pb-8">
                <i className="fa fa-exclamation-triangle" />
                { `Complete required field${errors.length > 1 ? 's' : ''} to be able to submit your project to Clora for review.` }
              </h3>
              <ul>
                { errors.map((error) => (
                  <li key={error} className="body-text-2 pb-4">{error}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-sm-3">
            <div className="masthead__actions-right style-guide">
              { this.renderEditLink(
                'Edit Project',
                null,
                'edit-project',
                'update',
                `btn standard-button
                ${project.status === 'employer_draft' ? 'btn-primary' : 'btn-default'}`,
              )}
              { permissions.destroy
                ? (
                  <a
                    role="link"
                    tabIndex={0}
                    id="delete-project"
                    onClick={this.handleDeleteProject}
                    className={`btn standard-button full-width
                    ${project.editable ? 'btn-primary' : 'btn-default'}
                    ${permissions.update ? '' : 'disabled'}`}
                    data-confirm="Are you sure you want to delete this project?"
                  >
                    🗑 Delete
                  </a>
                )
                : ''}
              { showRequestMatching
                ? (
                  <a
                    role="link"
                    tabIndex={0}
                    id="publish-project"
                    onClick={this.handlePublishProject}
                    className="btn publish-project btn-primary"
                    data-confirm="Are you sure you want to publish this project?"
                  >
                    Request Matching
                  </a>
                ) : ''}
              {
                (project.status !== 'employer_requested_matching' && permissions.unpublish
                  ? (
                    <a
                      role="link"
                      tabIndex={0}
                      id="unpublish-project"
                      onClick={this.handleUnpublishProject}
                      className={`btn btn-default unpublish-project
                      ${project.editable ? 'btn-primary' : 'btn-default disabled'}`}
                      data-confirm="Are you sure you want to retract this project?"
                    >
                      Retract Submission
                    </a>
                  )
                  : '')
              }

            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHoursPerWeek() {
    const { project, ownerOrCloraRep } = this.props;

    return (
      <Fragment>
        { ownerOrCloraRep || project.avg_time_required_range
          ? (
            <div className="commitment hours-per-week">
              <i className="fa fa-clock" />
              { project.avg_time_required_range
                ? `${project.avg_time_required_range} hours per week` : ''}
            </div>
          ) : this.renderEditLink('Average Time', 'start', null, 'update')}
      </Fragment>
    );
  }

  renderBudget() {
    const { project, ownerOrCloraRep } = this.props;
    const { budget_maximum: max, budget_minimum: min, employment_type: employmentType } = project;
    const budgetText = employmentType === 'full_time' ? 'Annual base salary range: ' : 'Hourly rate: ';
    const budget = max && min ? `${formatCurrency(min, 0)} - ${formatCurrency(max, 0)}` : null;
    return (
      <Fragment>
        { ownerOrCloraRep
          ? (
            <div className="commitment hourly-rate">
              <i className="fa fa-dollar-sign" />
              { budget ? `${budgetText} ${budget}`
                : this.renderEditLink(
                  employmentType === 'full_time' ? 'Base Salary' : 'Hourly Rate',
                  'logistics',
                  null,
                  'update',
                )}
            </div>
          ) : ''}
      </Fragment>
    );
  }

  renderTravelRequirement() {
    const { project, showProjectEmploymentType } = this.props;
    return (
      <div className="commitment travel">
        <i className="fa fa-plane" />
        { showProjectEmploymentType
          ? (`Travel Requirements: ${project.travel_location_names ? project.travel_location_names : 'N/A'}`)
          : (`Travel Requirements - ${project.travel_location_names ? project.travel_location_names : 'N/A'}`)}
      </div>
    );
  }

  renderLocationAndWorktype() {
    const { project } = this.props;
    return (
      <Fragment>
        {project.work_location
        && (
        <div className="commitment work-location">
          <i className="fa fa-map-marker-alt" />
          { `${project.office_and_worktype_information}` }
        </div>
        )}
      </Fragment>
    );
  }

  renderSeniority() {
    const {
      project, ownerOrCloraRep, permissions, projectResourceId,
    } = this.props;
    return (
      <Fragment>
        { (project.seniority.length || ownerOrCloraRep)
          ? (
            <div className="commitment seniority">
              <i className="fa fa-certificate" />
              <div className="levels">
                { project.seniority.length ? project.level_names
                  : this.renderEditLink('Experience Levels', 'start', null, 'update')}
              </div>
              { permissions.update
                ? (
                  <a
                    className="edit-budget-button"
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title="Edit Seniority/Budget"
                    href={`/projects/${projectResourceId}/edit?step=start&from_project_page=true`}
                  >
                    <i className="fa fa-edit" />
                  </a>
                ) : ''}
            </div>
          ) : ''}
      </Fragment>
    );
  }

  renderDuration() {
    const {
      project, ownerOrCloraRep, permissions, projectResourceId,
    } = this.props;

    return (
      <Fragment>
        { project.duration_range || ownerOrCloraRep
          ? (
            <div className="commitment duration">
              <i className="fa fa-calendar-day" />
              { project.duration_range
                ? `${project.duration_range} ${project.duration_range > 1 ? ' months' : ' month'}`
                : this.renderEditLink('Duration Range', 'start', null, 'update')}
              { permissions.update
                ? (
                  <a
                    className="edit-logistics-button"
                    data-toggle="tooltip"
                    data-placement="bottom"
                    title="Edit Logistics"
                    href={`/projects/${projectResourceId}/edit?step=start&from_project_page=true`}
                  >
                    <i className="fa fa-edit" />
                  </a>
                ) : ''}
            </div>
          ) : ''}
      </Fragment>
    );
  }

  renderOverview() {
    const {
      project, userType, ownerOrCloraRep, permissions, projectResourceId,
      showProjectEmploymentType, isCollaborator,
    } = this.props;

    return (
      <div className="project-overview">
        {/* left side */}
        <div className="project-overview-data">
          <div className="g-row">
            { userType === 'rep' && project.higher_level_roles.length
              ? (
                <div className="project-role body-text-2 normal-weight muted-gray-text mb-2">
                  {`Relevant Job Role: ${project.higher_level_roles[0]}`}
                </div>
              ) : ''}
            { userType === 'rep'
              && (
              <div className="project-company body-text-2 normal-weight muted-gray-text">
                { project.job_title && `Relevant Job Titles(s): ${project.job_title}` }
                { project.job_title && project.company && ' - ' }
              </div>
              )}
          </div>

          <div className="g-row body-text-3">
            <h2 className="project-section-title">
              { this.renderEditLink('Logistics', 'logistics', null, 'update') }
            </h2>
          </div>
          {showProjectEmploymentType
            ? (
              <ProjectLogisticsInfo
                project={project}
                permissions={permissions}
                projectResourceId={projectResourceId}
                ownerOrCloraRep={ownerOrCloraRep}
                isCollaborator={isCollaborator}
              />
            )
            : (
              <Fragment>
                <div className="commitments g-row body-text-3">
                  <div className="g-column g-flex-fill mb-2">
                    {/* duration */}
                    {this.renderDuration()}
                    {/* hours */}
                    {!isCollaborator && this.renderHoursPerWeek()}
                  </div>

                  <div className="g-column g-flex-fill">
                    {/* seniority */}
                    {this.renderSeniority()}

                    {/* hourly rate */}
                    { ownerOrCloraRep
                      ? (
                        <div className="commitment hourly-rate">
                          <i className="fa fa-dollar-sign" />
                          { project.budget_minimum
                    && (project.employment_type === 'full_time'
                      ? `${project.budget_minimum.toLocaleString()} - `
                      : `${project.budget_minimum} - `)}
                          { project.budget_maximum
                     && (project.employment_type === 'full_time'
                       ? `$${project.budget_maximum.toLocaleString()} / year`
                       : `$${project.budget_maximum} / hour`)}
                          { !project.budget_maximum
                    && (project.employment_type === 'full_time'
                      ? this.renderEditLink('Base Salary', 'logistics', null, 'update')
                      : this.renderEditLink('Hourly Rate', 'logistics', null, 'update'))}
                        </div>
                      ) : ''}
                  </div>
                </div>

                <div className="commitments g-row body-text-3">
                  <div className="g-column g-flex-fill mb-2">
                    {/* location and workplace type */}
                    {this.renderLocationAndWorktype()}
                    {/* travel */}
                    {this.renderTravelRequirement()}
                  </div>
                </div>
              </Fragment>
            )}
        </div>
        {/* right side */}
        { permissions.edit || permissions.duplicate
          ? (
            <div className="project-overview-buttons">
              <div className="g-row--always">
                { permissions.edit
                  ? (
                    <a
                      className="edit-project-button"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      title="Edit Project"
                      href={`/projects/${projectResourceId}/edit?from_project_page=true`}
                    >
                      <i className="fa fa-edit" />
                    </a>
                  ) : ''}
                { permissions.duplicate
                  ? (
                    <a
                      role="link"
                      tabIndex={0}
                      className="duplicate-project-button"
                      data-toggle="tooltip"
                      data-placement="bottom"
                      title="Duplicate Project"
                      onClick={this.handleDuplicateProject}
                    >
                      <i className="fa fa-clone" />
                    </a>
                  ) : ''}
              </div>
            </div>
          ) : ''}
      </div>
    );
  }

  renderAboutSection() {
    const { project } = this.props;

    return (
      <div className="project-description">
        <h2 className="project-section-title">
          { this.renderEditLink('About', 'description', null, 'update') }
        </h2>
        <div className="about">
          <span>{ project.description }</span>
        </div>
      </div>
    );
  }

  renderResponsibilities() {
    const { project } = this.props;

    return [
      <div className="project-responsibilities">
        <h2 className="project-section-title">
          { this.renderEditLink('Responsibilities', 'description', null, 'update') }
        </h2>
        { project.responsibility_areas.length
          ? (
            <ul>
              { project.responsibility_areas.map((ra) => (
                <li key={ra} className="responsibility_area">{ decodeAmpersands(ra) }</li>
              ))}
            </ul>
          )
          : ''}
      </div>,
      <div className="project-ideal-candidate">
        <h2 className="project-section-title">
          { this.renderEditLink('Ideal Candidate', 'description', null, 'update') }
        </h2>
        <ul>
          { project.ideal_candidate_list.map((ra) => (
            <li key={ra} className="responsibility_area">{ decodeAmpersands(ra) }</li>
          ))}
        </ul>
      </div>,
    ];
  }

  renderAdminSection() {
    const { project } = this.props;
    const { showAdminInfo } = this.state;

    return (
      <div className="mt-3 mb-4 admin">
        <h2 className="project-section-title mb-2">
          <a
            className="btn-view-admin-info"
            onClick={this.toggleShowAdminInfo}
            role="button"
            tabIndex={0}
          >
            {showAdminInfo ? 'Hide' : 'Show'}
            {' '}
            Admin Info
          </a>
        </h2>
        {showAdminInfo
          && (
          <Fragment>
            <h2 className="mb-12">
              { this.renderEditLink('Update', 'admin', null, 'update') }
            </h2>
            <ul>
              <li key="take-rate" id="take-rate">
                Clora take rate:
                {' '}
                { project.clora_take_rate * 100 }
                %
              </li>

              { }
              <li key="consultant-take-rate" id="consultant-take-rate">
                Consultant take home:
                { project.consultant_take_home_minimum
                  ? `$${parseInt(project.consultant_take_home_minimum, 0)} - ` : ' ?' }
                { project.consultant_take_home_maximum
                  ? `$${parseInt(project.consultant_take_home_maximum, 0)}` : '' }
                /hour
              </li>
              { }

              { project.have_internal_budget_approval
                ? (
                  <li key="status" className="status">
                    <span id="status">The project has internal budget approval</span>
                  </li>
                ) : ''}

              { project.consultant_working_with
                ? (
                  <li key="consultant-working-with" className="consultant-working-with">
                    Working with:
                    {' '}
                    { project.consultant_working_with }
                  </li>
                ) : '' }

              { project.tax_type
                ? (
                  <li key="key_type" id="tax_type">
                    Tax filing:
                    {' '}
                    { project.tax_type === 'n/a' ? 'TBD' : project.tax_type_collection }
                  </li>
                ) : '' }

              { project.consultant_needs_sign_agreement
                ? <li key="non_compete" id="non_compete">Need to sign a non compete agreement.</li>
                : '' }
            </ul>

            {/* start date */}
            { project.approximate_start_date_local
              ? (
                <div className="commitment approximate-start-date-local">
                  <i className="fa fa-hourglass-start" style={{ marginRight: '10px' }} />
                  Starting
                  {' '}
                  { project.start_date_display }
                </div>
              ) : '' }
          </Fragment>
          )}
      </div>
    );
  }

  renderSkill(id, badgeClass, skillName, skills, notApplicable) {
    return (
      <div id={id} className="skill">
        <div className="body-text-3 medium-weight skill-name">
          { this.renderEditLink(skillName, 'qualifications', null, 'update') }
        </div>
        { notApplicable
          ? <span className="body-text-3 muted-gray-text">Not Applicable</span>
          : skills.map((skill) => (
            <span className={`badge small ${badgeClass} ${skill[1] ? 'priority' : ''}`}>
              { skill[0] }
              { skill[1] ? <strong><i>*Must have</i></strong> : '' }
            </span>
          ))}
      </div>
    );
  }

  renderSkills() {
    const { project, ownerOrCloraRep } = this.props;

    return (
      <div className="skills">
        <div className="skills-header-container">
          <h2 className="project-section-title me-2">Skills</h2>
          {this.handleStepNavigation('Edit Qualification', 'qualifications')}
        </div>
        { project.area_of_expertises.length || ownerOrCloraRep
          ? this.renderSkill('functional-areas', 'purple-base', 'Functional Areas', project.area_of_expertises.map((aoe) => (
            [aoe?.name, project.priority_expertise_tags.find((paoe) => paoe === aoe.name)])), false) : '' }

        { (project.therapeutic_areas.length && !project.therapeutic_areas_not_applicable)
            || ownerOrCloraRep ? this.renderSkill(
            'therapeutic-areas',
            '',
            'Therapeutic Areas',
            project.therapeutic_areas.map((ta) => (
              [ta?.name, project.priority_expertise_tags.find((pta) => pta === ta.name)])),
            project.therapeutic_areas_not_applicable,
          ) : '' }
        { (project.therapies.length && !project.therapies_not_applicable) || ownerOrCloraRep
          ? this.renderSkill(
            'therapies',
            '',
            'Product Technologies',
            project.therapies.map((t) => (
              [t?.name, project.priority_expertise_tags.find((pt) => pt === t.name)])),
            project.therapies_not_applicable,
          ) : '' }
        { (project.disease_codes.length && !project.disease_codes_not_applicable)
            || ownerOrCloraRep ? this.renderSkill(
            'disease_codes',
            '',
            'Diseases/Indications',
            project.disease_codes.map((d) => (
              [d?.name, project.priority_expertise_tags.find((pd) => pd === d.name)])),
            project.disease_codes_not_applicable,
          ) : '' }
        { (project.development_phases.length && !project.development_phases_not_applicable)
            || ownerOrCloraRep ? this.renderSkill(
            'development_phases',
            '',
            'Development Phases',
            project.development_phases.map((d) => (
              [d?.name, project.priority_expertise_tags.find((pd) => pd === d.name)])),
            project.development_phases_not_applicable,
          ) : '' }
        { (project.regulatory_pathways.length && !project.regulatory_pathways_not_applicable)
            || ownerOrCloraRep ? this.renderSkill(
            'regulatory_pathways',
            '',
            'Regulatory Pathways',
            project.regulatory_pathways.map((r) => (
              [r?.name, project.priority_expertise_tags.find((pr) => pr === r.name)])),
            project.regulatory_pathways_not_applicable,
          ) : '' }
      </div>
    );
  }

  renderQualifications() {
    const {
      ownerOrCloraRep,
      project: {
        accreditations, degrees, priority_expertise_tags, languages,
      },
    } = this.props;
    const hasQualifications = degrees.length || accreditations.length || languages.length || ownerOrCloraRep;

    const renderQualificationSection = (id, title, items, isPriorityCheck = false) => (
      <div key={id} id={id} className="qualification">
        <div className="body-text-3 medium-weight qualification-name">
          {this.renderEditLink(title, 'qualifications', null, 'update')}
        </div>
        <ul className="normal-weight">
          {items.map((item) => {
            const itemName = typeof item === 'object' ? item?.name : item;
            if (!itemName) return null;
            const isPriority = isPriorityCheck && priority_expertise_tags.includes(itemName);
            return (
              <li key={itemName} className={isPriority ? 'priority' : ''}>
                {itemName}
                {isPriority && <strong><i>&nbsp;*Must have</i></strong>}
              </li>
            );
          })}
        </ul>
      </div>
    );

    return hasQualifications && (
      <div className="qualifications">
        <h2 className="project-section-title">Qualifications</h2>
        {degrees.length || ownerOrCloraRep ? renderQualificationSection('education-levels', 'Education', degrees) : null}
        {accreditations.length || ownerOrCloraRep ? renderQualificationSection('accreditations', 'Certifications', accreditations, true) : null}
        {languages.length || ownerOrCloraRep ? renderQualificationSection('languages', 'Languages', languages) : null}
      </div>
    );
  }

  renderProjectDisplay() {
    const { project, showPublishedModal, userType } = this.props;

    return (
      <Fragment>
        { showPublishedModal ? <ProjectPublishedModal referralUrl="/referrals" /> : '' }
        <section className={`project-view project ${project.status === 'clora_matching' || project.open_marketplace ? 'open-opportunity' : 'upcoming-opportunity'} ${project.open_marketplace ? 'open-marketplace' : ''} row`}>
          { this.renderOverview() }

          <div className="row body-text-2">
            <div className="col-sm-12 col-md-7 col-lg-8">
              { this.renderAboutSection() }
              { this.renderResponsibilities() }
              { userType === 'rep' ? this.renderAdminSection() : '' }
            </div>
            <div className="col-sm-12 col-md-5 col-lg-4">
              { this.renderSkills() }
              { this.renderQualifications() }
            </div>
          </div>

          { userType === 'rep'
            ? (
              <div className="updated-at-container mt-60 body-text-3">
                { project.owner
                  ? <div className="fs-hide">{ project.owner_display }</div>
                  : (
                    <div>
                      Created:
                      { project.created_at }
                    </div>
                  )}
              </div>
            ) : ''}
        </section>
      </Fragment>
    );
  }

  renderStartProject() {
    const { project } = this.props;

    return (
      <div className="no-padding-white-container">
        <div className="start-your-own-project">
          { project.feedbacks.length && !(project.status === 'employer_draft' || project.status === 'employer_requested_matching')
            ? (
              <h2 className="mb-3">
                Leaving feedback is no longer available and no feedbacks are available to be displayed
              </h2>
            )
            : (
              <h2 className="mb-3">
                <span className="fs-hide body-text-1 demibold">
                  { project.owner || project.owner_email }
                </span>
                {' '}
                is using Clora to find expert life sciences consultants for this project.
              </h2>
            )}
          <a href="/projects/add_project" className="btn btn-primary">
            Start Your Own Project
          </a>
        </div>
      </div>
    );
  }

  render() {
    const { project } = this.props;

    if (!project) {
      return (
        <div className="loading">
          <img src={loading} alt="Loading_image" />
        </div>
      );
    }

    return (
      <div className="g-flex-8 project-info-column">
        { project.error_messages.length ? this.renderErrors() : '' }
        <h3 className="mb-3">Project Details</h3>
        <div className="white-container project-info-box box-shadow-1">
          { this.renderProjectDisplay() }
        </div>
      </div>
    );
  }
}

ProjectSummary.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  project: PropTypes.shape({}),
  projectResourceId: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}),
  ownerOrCloraRep: PropTypes.bool,
  showPublishedModal: PropTypes.bool,
  userType: PropTypes.string.isRequired,
  showProjectEmploymentType: PropTypes.bool,
};

ProjectSummary.defaultProps = {
  project: null,
  permissions: {},
  ownerOrCloraRep: false,
  showPublishedModal: false,
  showProjectEmploymentType: false,
};

export default ProjectSummary;
